// Connection to the Choir Durable Object, with clock sync and reconnection.
// If the network is unavailable the client falls back to a LocalChoir that
// simulates the same protocol in the browser, so the planet is never silent.

import { ClockSync } from "../shared/clock";
import type { ClientMsg, ServerMsg } from "../shared/protocol";
import { Emitter } from "./emitter";
import { LocalChoir } from "./local-choir";

export type NetStatus = "connecting" | "live" | "reconnecting" | "offline";

export interface NetEvents extends Record<string, unknown> {
  message: ServerMsg;
  status: NetStatus;
  rtt: number;
}

export function localNow(): number {
  return performance.timeOrigin + performance.now();
}

export class Net extends Emitter<NetEvents> {
  readonly clock = new ClockSync();
  status: NetStatus = "connecting";
  private ws: WebSocket | null = null;
  private local: LocalChoir | null = null;
  private attempts = 0;
  private pingTimer: number | undefined;
  private welcomeTimer: number | undefined;
  private closedByUs = false;
  private everLive = false;

  constructor(readonly room: string, private readonly opts: { forceOffline?: boolean } = {}) {
    super();
  }

  connect(): void {
    if (this.opts.forceOffline) {
      this.goOffline();
      return;
    }
    this.open();
  }

  private url(): string {
    const proto = location.protocol === "https:" ? "wss:" : "ws:";
    return `${proto}//${location.host}/api/room/${encodeURIComponent(this.room)}/ws`;
  }

  private setStatus(s: NetStatus): void {
    if (this.status === s) return;
    this.status = s;
    this.emit("status", s);
  }

  private open(): void {
    this.closedByUs = false;
    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url());
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    this.setStatus(this.everLive ? "reconnecting" : "connecting");
    window.clearTimeout(this.welcomeTimer);
    this.welcomeTimer = window.setTimeout(() => {
      // No welcome in time: treat as failed.
      if (this.status !== "live") ws.close();
    }, 7000);

    ws.onopen = () => {
      this.clock.reset();
      // A quick burst of pings gets a good clock estimate before the first bar.
      for (let i = 0; i < 6; i++) window.setTimeout(() => this.ping(), i * 120);
      window.clearInterval(this.pingTimer);
      this.pingTimer = window.setInterval(() => this.ping(), 2500);
    };
    ws.onmessage = (e) => {
      let msg: ServerMsg;
      try {
        msg = JSON.parse(typeof e.data === "string" ? e.data : "") as ServerMsg;
      } catch {
        return;
      }
      if (msg.t === "pong") {
        const s = this.clock.add(msg.c, msg.s, localNow());
        this.emit("rtt", s.rtt);
        return;
      }
      if (msg.t === "welcome") {
        window.clearTimeout(this.welcomeTimer);
        this.attempts = 0;
        this.everLive = true;
        // Seed the clock with the welcome timestamp until pongs arrive.
        if (!this.clock.ready) this.clock.add(localNow() - 50, msg.now, localNow());
        this.setStatus("live");
      }
      this.emit("message", msg);
    };
    ws.onclose = () => {
      window.clearInterval(this.pingTimer);
      window.clearTimeout(this.welcomeTimer);
      this.ws = null;
      if (this.closedByUs) return;
      this.scheduleReconnect();
    };
    ws.onerror = () => {
      // onclose follows.
    };
  }

  private scheduleReconnect(): void {
    this.attempts++;
    if (!this.everLive && this.attempts >= 2) {
      this.goOffline();
      return;
    }
    this.setStatus("reconnecting");
    const delay = Math.min(15000, 800 * 2 ** Math.min(this.attempts, 5)) * (0.75 + Math.random() * 0.5);
    window.setTimeout(() => this.open(), delay);
  }

  private goOffline(): void {
    if (this.local) return;
    this.clock.reset();
    this.clock.add(localNow(), localNow(), localNow());
    this.local = new LocalChoir(this.room, (msg) => this.emit("message", msg));
    this.setStatus("offline");
    this.local.start();
  }

  private ping(): void {
    this.send({ t: "ping", c: localNow() });
  }

  send(msg: ClientMsg): void {
    if (this.local) {
      this.local.receive(msg);
      return;
    }
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  serverNow(): number {
    return this.clock.serverNow(localNow());
  }

  get isOffline(): boolean {
    return !!this.local;
  }

  close(): void {
    this.closedByUs = true;
    window.clearInterval(this.pingTimer);
    this.ws?.close();
    this.local?.stop();
  }
}
